import React, { useState } from 'react'
import './AdminPage.css'
import RegressionTestingPanel from './RegressionTestingPanel'
import GeometrySection from './sections/GeometrySection'
import LibrarySection from './sections/LibrarySection'
import LoadsSection from './sections/LoadsSection'
import MaterialsSection from './sections/MaterialsSection'
import SamplesSection from './sections/SamplesSection'
import StructuraBitsSection from './sections/StructuraBitsSection'
import UsersSection from './sections/UsersSection'
import { getSessionHeaders as sharedGetSessionHeaders } from '@structura/services'
import { useTranslation } from '@structura/i18n'
import { ALLOWED_MATERIAL_CATEGORIES, TEMPLATE_MATERIAL_TYPE } from './materialCategories'
import type { AppMaterial, MaterialCategory } from './materialCategories'

export type { AppMaterial, MaterialCategory }

// Stable identity on purpose: child effects depend on it, so a per-render closure would refetch
// on every AdminPage render. Authorization is AdminRoute's job (admin by e-mail).
const getSessionHeaders = (): HeadersInit => sharedGetSessionHeaders()

export interface AdminPageProps {
  // Which per-material beam app this admin page belongs to. Drives which material
  // categories are visible (concrete = concrete + reinforcement + prestressing;
  // wood = timber; steel = steel) and which materialType filter is passed when
  // loading beam templates and cross-section templates.
  appMaterial: AppMaterial
}

const Admin: React.FC<AdminPageProps> = ({ appMaterial }) => {
  const { t } = useTranslation()
  const allowedCategories: readonly MaterialCategory[] = ALLOWED_MATERIAL_CATEGORIES[appMaterial]
  const templateMaterialType: string = TEMPLATE_MATERIAL_TYPE[appMaterial]

  const [activeSection, setActiveSection] = useState<'samples' | 'geometry' | 'loads' | 'library' | 'materials' | 'structurabits' | 'users' | 'regression'>('samples')
  const [error, setError] = useState<string | null>(null)

  return (
    <div className="container-fluid mt-4 admin-page">
      <div className="row">
        <div className="col-12">
          <h1 className="mb-4">
            <i className="bi bi-gear-fill me-2"></i>
            {t('admin.panelTitle')}
          </h1>
        </div>
      </div>

      <div className="row">
        <div className="col-md-3">
          <div className="list-group admin-menu">
            <button
              className={`list-group-item list-group-item-action ${activeSection === 'samples' ? 'active' : ''}`}
              onClick={() => setActiveSection('samples')}
            >
              <i className="bi bi-file-earmark-pdf me-2"></i>
              {t('admin.menu.samples')}
            </button>
            <button
              className={`list-group-item list-group-item-action ${activeSection === 'geometry' ? 'active' : ''}`}
              onClick={() => setActiveSection('geometry')}
            >
              <i className="bi bi-rulers me-2"></i>
              {t('admin.menu.geometry')}
            </button>
            <button
              className={`list-group-item list-group-item-action ${activeSection === 'materials' ? 'active' : ''}`}
              onClick={() => setActiveSection('materials')}
            >
              <i className="bi bi-gear-wide-connected me-2"></i>
              {t('admin.menu.materials')}
            </button>
            <button
              className={`list-group-item list-group-item-action ${activeSection === 'loads' ? 'active' : ''}`}
              onClick={() => setActiveSection('loads')}
            >
              <i className="bi bi-arrow-down-up me-2"></i>
              {t('admin.menu.loads')}
            </button>
            <button
              className={`list-group-item list-group-item-action ${activeSection === 'library' ? 'active' : ''}`}
              onClick={() => setActiveSection('library')}
            >
              <i className="bi bi-box-seam me-2"></i>
              {t('admin.menu.library')}
            </button>
            <button
              className={`list-group-item list-group-item-action ${activeSection === 'structurabits' ? 'active' : ''}`}
              onClick={() => setActiveSection('structurabits')}
            >
              <i className="bi bi-stars me-2"></i>
              {t('admin.menu.structurabits')}
            </button>
            <button
              className={`list-group-item list-group-item-action ${activeSection === 'users' ? 'active' : ''}`}
              onClick={() => setActiveSection('users')}
            >
              <i className="bi bi-people me-2"></i>
              {t('admin.menu.users')}
            </button>
            <button
              className={`list-group-item list-group-item-action ${activeSection === 'regression' ? 'active' : ''}`}
              onClick={() => setActiveSection('regression')}
            >
              <i className="bi bi-bug me-2"></i>
              {t('admin.menu.regression')}
            </button>
          </div>
        </div>

        <div className="col-md-9">
          {error && (
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              {error}
              <button type="button" className="btn-close" onClick={() => setError(null)}></button>
            </div>
          )}

          {activeSection === 'samples' && (
            <SamplesSection getSessionHeaders={getSessionHeaders} onError={setError} />
          )}

          {activeSection === 'geometry' && (
            <GeometrySection
              templateMaterialType={templateMaterialType}
              getSessionHeaders={getSessionHeaders}
            />
          )}

          {activeSection === 'materials' && (
            <MaterialsSection
              allowedCategories={allowedCategories}
              getSessionHeaders={getSessionHeaders}
            />
          )}

          {activeSection === 'loads' && (
            <LoadsSection getSessionHeaders={getSessionHeaders} />
          )}

          {activeSection === 'library' && (
            <LibrarySection
              templateMaterialType={templateMaterialType}
              getSessionHeaders={getSessionHeaders}
              onError={setError}
            />
          )}

          {activeSection === 'structurabits' && (
            <StructuraBitsSection
              appMaterial={appMaterial}
              getSessionHeaders={getSessionHeaders}
            />
          )}

          {activeSection === 'users' && (
            <UsersSection getSessionHeaders={getSessionHeaders} />
          )}

          {activeSection === 'regression' && (
            <RegressionTestingPanel />
          )}
        </div>
      </div>

    </div>
  )
}

export default Admin

